import React from 'react';
import {
  saBillingCanManage,
  saBillingFormatDateBR,
  saBillingFormatDateTimeBR,
  saBillingFormatError,
  saBillingFormatInvoiceDisplayStatus,
  saBillingFormatMoneyBRL,
  saBillingFormatPaymentMethod,
  saBillingFormatSubscriptionStatus,
  saBillingMapGlobalInvoice,
} from './billing-support.jsx';
import { ApiErrorState, billingService, tenantService, useGlobalBillingInvoices } from '../services.jsx';
// Billing invoices — lista global, detalhe tenant-scoped e gestão (F4).
// Sem import/export. Sem HTTP direto. Sem cálculo financeiro.
  const { useState, useEffect, useRef, useMemo } = React;

  const FOCUSABLE_SELECTOR = [
    'button:not([disabled])',
    '[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function getFocusableElements(container) {
    if (!container) return [];
    const nodes = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR));
    return nodes.filter((el) => {
      if (!el || el.disabled) return false;
      const style = window.getComputedStyle(el);
      if (style.visibility === 'hidden' || style.display === 'none') return false;
      if (el.offsetParent === null && style.position !== 'fixed') return false;
      return true;
    });
  }

  function trapFocus(event, container) {
    if (!container || event.key !== 'Tab') return;
    const focusable = getFocusableElements(container);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  const INVOICE_FIELD_IDS = {
    tenant_id: 'billing-invoice-tenant-search',
    description: 'billing-invoice-description',
    amount: 'billing-invoice-amount',
    due_date: 'billing-invoice-due-date',
    subscription_id: 'billing-invoice-subscription',
    payment_method: 'billing-invoice-payment-method',
    notes: 'billing-invoice-notes',
    reason: 'billing-invoice-reason',
  };

  const INVOICE_ERROR_FIELD_ORDER = [
    'tenant_id', 'description', 'amount', 'due_date',
    'subscription_id', 'payment_method', 'notes', 'reason',
  ];

  function focusFirstFormError(errors) {
    if (!errors || typeof errors !== 'object') return;
    for (let i = 0; i < INVOICE_ERROR_FIELD_ORDER.length; i += 1) {
      const key = INVOICE_ERROR_FIELD_ORDER[i];
      if (errors[key]) {
        const el = document.getElementById(INVOICE_FIELD_IDS[key]);
        if (el && typeof el.focus === 'function') {
          el.focus();
          return;
        }
      }
    }
  }

  const ACCEPTED_QUERY_KEYS = [
    'tenant_id',
    'status',
    'display_status',
    'tenant_status',
    'due_date_from',
    'due_date_to',
    'paid_from',
    'paid_to',
    'created_from',
    'created_to',
    'plan_code',
    'payment_method',
    'has_subscription',
    'search',
    'ordering',
    'page',
    'page_size',
  ];

  const TENANT_STATUS_LABELS = {
    active: 'Ativo',
    suspended: 'Suspenso',
    archived: 'Arquivado',
  };

  const PERSISTED_STATUS_LABELS = {
    open: 'Aberta',
    paid: 'Marcada como paga',
    cancelled: 'Cancelada',
  };

  const HISTORY_EVENT_LABELS = {
    'billing.invoice.created': 'Fatura criada',
    'billing.invoice.updated': 'Fatura atualizada',
    'billing.invoice.marked_paid': 'Marcada como paga',
    'billing.invoice.cancelled': 'Fatura cancelada',
  };

  const PAYMENT_METHODS = [
    'not_informed',
    'manual',
    'pix',
    'bank_transfer',
    'boleto',
    'card',
    'other',
  ];

  const DISPLAY_STATUSES = ['open', 'overdue', 'paid', 'cancelled'];
  const PERSISTED_STATUSES = ['open', 'paid', 'cancelled'];
  const TENANT_STATUSES = ['active', 'suspended', 'archived'];

  const ORDERING_OPTIONS = [
    { value: '-created_at', label: 'Criação (mais recente)' },
    { value: 'created_at', label: 'Criação (mais antiga)' },
    { value: 'due_date', label: 'Vencimento (mais próximo)' },
    { value: '-due_date', label: 'Vencimento (mais distante)' },
    { value: '-paid_at', label: 'Pagamento (mais recente)' },
    { value: 'paid_at', label: 'Pagamento (mais antigo)' },
    { value: '-amount', label: 'Valor (maior)' },
    { value: 'amount', label: 'Valor (menor)' },
    { value: '-number', label: 'Número (Z–A)' },
    { value: 'number', label: 'Número (A–Z)' },
    { value: 'tenant_name', label: 'Tenant (A–Z)' },
    { value: '-tenant_name', label: 'Tenant (Z–A)' },
    { value: 'status', label: 'Status (A–Z)' },
    { value: '-status', label: 'Status (Z–A)' },
  ];

  const DEFAULT_LIST_STATE = {
    search: '',
    display_status: '',
    status: '',
    tenant_status: '',
    plan_code: '',
    payment_method: '',
    has_subscription: '',
    due_date_from: '',
    due_date_to: '',
    paid_from: '',
    paid_to: '',
    created_from: '',
    created_to: '',
    ordering: '-created_at',
    page: 1,
    page_size: 20,
  };

  function money(value) {
    if (typeof saBillingFormatMoneyBRL === 'function') {
      return saBillingFormatMoneyBRL(value);
    }
    return '—';
  }

  function dateBR(value) {
    if (typeof saBillingFormatDateBR === 'function') {
      return saBillingFormatDateBR(value);
    }
    return '—';
  }

  function dateTimeBR(value) {
    if (typeof saBillingFormatDateTimeBR === 'function') {
      return saBillingFormatDateTimeBR(value);
    }
    return '—';
  }

  function formatPayment(code) {
    if (typeof saBillingFormatPaymentMethod === 'function') {
      return saBillingFormatPaymentMethod(code);
    }
    return code || '—';
  }

  function formatDisplayStatus(code) {
    if (typeof saBillingFormatInvoiceDisplayStatus === 'function') {
      return saBillingFormatInvoiceDisplayStatus(code);
    }
    return code || '—';
  }

  function formatError(error, fallback) {
    if (typeof saBillingFormatError === 'function') {
      return saBillingFormatError(error, fallback);
    }
    return (error && error.message) || fallback;
  }

  function formatSubscriptionStatus(code) {
    if (typeof saBillingFormatSubscriptionStatus === 'function') {
      return saBillingFormatSubscriptionStatus(code);
    }
    return code || '—';
  }

  function isValidDateOnly(value) {
    if (value === null || value === undefined || value === '') return false;
    const str = String(value).trim();
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
    if (!match) return false;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const local = new Date(year, month - 1, day);
    return (
      local.getFullYear() === year
      && local.getMonth() === month - 1
      && local.getDate() === day
    );
  }

  function isPositiveIntegerId(value) {
    if (value == null || value === '') return false;
    if (typeof value === 'number') {
      return Number.isInteger(value) && value > 0;
    }
    const str = String(value).trim();
    if (!/^\d+$/.test(str)) return false;
    const num = Number(str);
    return Number.isInteger(num) && num > 0;
  }

  function billingEntityKey(tenantId, entityId) {
    return `${tenantId}:${entityId}`;
  }

  function actorLabel(actor) {
    if (!actor || typeof actor !== 'object') return '—';
    if (actor.name) return actor.name;
    if (actor.email) return actor.email;
    if (actor.id != null) return `Usuário #${actor.id}`;
    return '—';
  }

  function snapshotDiffEntries(before, after) {
    const a = before && typeof before === 'object' ? before : {};
    const b = after && typeof after === 'object' ? after : {};
    const keys = Array.from(new Set([].concat(Object.keys(a), Object.keys(b))));
    return keys
      .filter((key) => String(a[key] ?? '') !== String(b[key] ?? ''))
      .map((key) => ({
        key,
        before: a[key] == null || a[key] === '' ? '—' : String(a[key]),
        after: b[key] == null || b[key] === '' ? '—' : String(b[key]),
      }));
  }

  function displayStatusTone(displayStatus) {
    if (displayStatus === 'overdue') return 'danger';
    if (displayStatus === 'paid') return 'success';
    if (displayStatus === 'cancelled') return 'neutral';
    if (displayStatus === 'open') return 'info';
    return 'neutral';
  }

  function displayStatusBadgeClass(tone) {
    if (tone === 'danger') return 'badge danger';
    if (tone === 'warning') return 'badge warning';
    if (tone === 'success') return 'badge success';
    if (tone === 'info') return 'badge info';
    return 'badge neutral';
  }

  function saBillingBuildInvoiceQuery(state) {
    const source = state && typeof state === 'object' ? state : {};
    const out = {};

    ACCEPTED_QUERY_KEYS.forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(source, key)) return;
      let value = source[key];
      if (value === null || value === undefined) return;

      if (key === 'search') {
        const trimmed = String(value).trim().slice(0, 200);
        if (trimmed) out.search = trimmed;
        return;
      }

      if (key === 'plan_code') {
        const trimmed = String(value).trim();
        if (trimmed && trimmed !== 'all') out.plan_code = trimmed;
        return;
      }

      if (key === 'has_subscription') {
        if (value === true || value === 'true') {
          out.has_subscription = 'true';
        } else if (value === false || value === 'false') {
          out.has_subscription = 'false';
        }
        return;
      }

      if (key === 'page') {
        const page = Math.max(1, Math.floor(Number(value)) || 1);
        out.page = page;
        return;
      }

      if (key === 'page_size') {
        let size = Math.floor(Number(value)) || 20;
        if (size < 1) size = 1;
        if (size > 100) size = 100;
        out.page_size = size;
        return;
      }

      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed || trimmed === 'all') return;
        out[key] = trimmed;
        return;
      }

      out[key] = value;
    });

    if (out.page == null) out.page = 1;
    if (out.page_size == null) out.page_size = 20;
    if (out.ordering == null) out.ordering = '-created_at';

    return out;
  }

  function saBillingBuildInvoicesViewModel(response) {
    const payload = response && typeof response === 'object' ? response : {};
    const results = Array.isArray(payload.results) ? payload.results : [];
    const count = Number(payload.count) || 0;
    const page = Number(payload.page) || 1;
    const pageSize = Number(payload.page_size) || 20;

    const rows = results.map((item) => {
      const mapped = typeof saBillingMapGlobalInvoice === 'function'
        ? saBillingMapGlobalInvoice(item)
        : Object.assign({}, item || {});

      const tenantName = mapped.tenant_name || '—';
      const tenantSchema = mapped.tenant_schema || '';
      const displayStatus = mapped.display_status || mapped.status || '';
      const tone = displayStatusTone(displayStatus);

      return Object.assign({}, mapped, {
        number_label: mapped.number || (mapped.id != null ? `#${mapped.id}` : '—'),
        tenant_label: tenantSchema ? `${tenantName} (${tenantSchema})` : tenantName,
        tenant_status_label: TENANT_STATUS_LABELS[mapped.tenant_status] || mapped.tenant_status || '—',
        description_label: mapped.description || '—',
        plan_label: mapped.plan_code_snapshot || '—',
        display_status_label: mapped.display_status_label || formatDisplayStatus(displayStatus),
        persisted_status_label: PERSISTED_STATUS_LABELS[mapped.status] || mapped.status || '—',
        display_status_tone: tone,
        amount_formatted: mapped.amount_formatted || money(mapped.amount),
        due_date_formatted: mapped.due_date_formatted || dateBR(mapped.due_date),
        payment_method_label: mapped.payment_method_label || formatPayment(mapped.payment_method),
        subscription_label: mapped.subscription_id != null && mapped.subscription_id !== ''
          ? `Assinatura #${mapped.subscription_id}`
          : 'Sem assinatura vinculada',
        paid_at_formatted: mapped.paid_at_formatted || dateTimeBR(mapped.paid_at),
        cancelled_at_formatted: mapped.cancelled_at_formatted || dateTimeBR(mapped.cancelled_at),
        created_at_formatted: dateTimeBR(mapped.created_at),
        updated_at_formatted: mapped.updated_at_formatted || dateTimeBR(mapped.updated_at),
        is_actionable: mapped.status === 'open',
      });
    });

    return {
      rows,
      count,
      page,
      page_size: pageSize,
      total_pages: Math.max(1, Math.ceil(count / pageSize)),
      is_empty: count === 0,
    };
  }

  function saBillingValidateInvoiceForm(form, mode, original) {
    const source = form && typeof form === 'object' ? form : {};
    const errors = {};
    const isCreate = mode === 'create';
    const isEdit = mode === 'edit';

    if (isCreate) {
      if (!isPositiveIntegerId(source.tenant_id)) {
        errors.tenant_id = 'Selecione um tenant.';
      }
    }

    const description = source.description == null ? '' : String(source.description).trim();
    if (!description) {
      errors.description = 'Informe a descrição.';
    } else if (description.length > 500) {
      errors.description = 'Descrição: máximo de 500 caracteres.';
    }

    const amountRaw = source.amount == null ? '' : String(source.amount).trim();
    if (!amountRaw) {
      errors.amount = 'Informe o valor.';
    } else if (/,/.test(amountRaw) || !/^\d+(\.\d{1,2})?$/.test(amountRaw)) {
      errors.amount = 'Use decimal positivo com ponto (ex.: 199.90).';
    } else {
      const amountNum = Number(amountRaw);
      if (!Number.isFinite(amountNum) || amountNum <= 0) {
        errors.amount = 'O valor deve ser maior que zero.';
      }
    }

    const dueDate = String(source.due_date || '').trim();
    if (!isValidDateOnly(dueDate)) {
      errors.due_date = 'Data de vencimento inválida.';
    }

    if (isCreate) {
      const subRaw = source.subscription_id;
      if (subRaw != null && subRaw !== '' && subRaw !== 'none') {
        const subNum = Number(subRaw);
        if (!Number.isInteger(subNum) || subNum <= 0) {
          errors.subscription_id = 'Assinatura inválida.';
        }
      }
    }

    const paymentMethod = String(source.payment_method || '').trim();
    if (!PAYMENT_METHODS.includes(paymentMethod)) {
      errors.payment_method = 'Forma de pagamento inválida.';
    }

    const notes = source.notes == null ? '' : String(source.notes);
    if (notes.length > 2000) {
      errors.notes = 'Observações: máximo de 2000 caracteres.';
    }

    const reason = source.reason == null ? '' : String(source.reason).trim();
    if (reason.length < 5 || reason.length > 500) {
      errors.reason = 'Motivo deve ter entre 5 e 500 caracteres.';
    }

    const valid = Object.keys(errors).length === 0;
    let payload = null;

    if (valid) {
      if (isCreate) {
        let subscriptionId = null;
        const subRaw = source.subscription_id;
        if (subRaw != null && subRaw !== '' && subRaw !== 'none') {
          subscriptionId = Number(subRaw);
        }
        payload = {
          description,
          amount: amountRaw,
          currency: 'BRL',
          due_date: dueDate,
          subscription_id: subscriptionId,
          payment_method: paymentMethod,
          notes,
          reason,
        };
      } else if (isEdit) {
        const orig = original && typeof original === 'object' ? original : {};
        payload = { reason };

        const origDescription = orig.description == null ? '' : String(orig.description);
        if (description !== origDescription) payload.description = description;

        const origAmount = orig.amount == null ? '' : String(orig.amount);
        if (amountRaw !== origAmount) payload.amount = amountRaw;

        const origCurrency = orig.currency == null ? 'BRL' : String(orig.currency);
        if ('BRL' !== origCurrency) payload.currency = 'BRL';

        const origDue = orig.due_date == null ? '' : String(orig.due_date);
        if (dueDate !== origDue) payload.due_date = dueDate;

        const origPayment = orig.payment_method == null ? '' : String(orig.payment_method);
        if (paymentMethod !== origPayment) payload.payment_method = paymentMethod;

        const origNotes = orig.notes == null ? '' : String(orig.notes);
        if (notes !== origNotes) payload.notes = notes;
      }
    }

    return { valid, errors, payload };
  }

  function validateDateRange(from, to) {
    if (!from && !to) return null;
    if (from && !isValidDateOnly(from)) return 'Data inicial inválida.';
    if (to && !isValidDateOnly(to)) return 'Data final inválida.';
    if (from && to && from > to) return 'A data inicial deve ser anterior ou igual à final.';
    return null;
  }

  function DetailField({ label, value }) {
    return (
      <div className="billing-invoice-detail-field">
        <div className="billing-invoice-detail-label">{label}</div>
        <div className="billing-invoice-detail-value">{value == null || value === '' ? '—' : value}</div>
      </div>
    );
  }

  function FieldError({ id, message }) {
    if (!message) return null;
    return (
      <div id={id} className="billing-invoice-field-error" role="alert">
        {message}
      </div>
    );
  }

  function InvoiceFormFields({
    form,
    errors,
    onChange,
    mode,
    selectedTenant,
    tenantSearch,
    onTenantSearchChange,
    tenantResults,
    tenantLoading,
    onSelectTenant,
    onClearTenant,
    tenantSubscriptions,
    subscriptionsLoading,
  }) {
    const tenantResultsId = 'billing-invoice-tenant-results';
    const tenantStatusId = 'billing-invoice-tenant-search-status';
    return (
      <div className="billing-invoice-form-grid">
        {mode === 'create' && (
          <div className="billing-invoice-form-span">
            <label htmlFor="billing-invoice-tenant-search">Tenant</label>
            {selectedTenant ? (
              <div className="billing-invoice-tenant-selected">
                <div>
                  <strong>{selectedTenant.name}</strong>
                  <div className="billing-invoices-muted">
                    {selectedTenant.schema_name}
                    {selectedTenant.plan && selectedTenant.plan.name
                      ? ` · Plano ${selectedTenant.plan.name}`
                      : selectedTenant.plan && selectedTenant.plan.id
                        ? ` · Plano ${selectedTenant.plan.id}`
                        : ''}
                    {selectedTenant.operational_status
                      ? ` · ${selectedTenant.operational_status}`
                      : ''}
                  </div>
                </div>
                <button type="button" className="btn" onClick={onClearTenant}>Trocar</button>
              </div>
            ) : (
              <>
                <input
                  id="billing-invoice-tenant-search"
                  type="search"
                  value={tenantSearch}
                  onChange={(e) => onTenantSearchChange(e.target.value)}
                  placeholder="Buscar tenant por nome ou schema"
                  maxLength={200}
                  aria-invalid={Boolean(errors.tenant_id)}
                  aria-describedby={
                    errors.tenant_id
                      ? 'billing-invoice-tenant-search-error'
                      : tenantStatusId
                  }
                  aria-controls={tenantResults.length ? tenantResultsId : undefined}
                  aria-busy={tenantLoading || undefined}
                />
                <div
                  id={tenantStatusId}
                  className="billing-invoices-muted"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {tenantLoading
                    ? 'Buscando tenants…'
                    : tenantResults.length > 0
                      ? `${tenantResults.length} resultado${tenantResults.length === 1 ? '' : 's'}`
                      : tenantSearch.trim() && !tenantLoading
                        ? 'Nenhum tenant encontrado.'
                        : ''}
                </div>
                {!tenantLoading && tenantResults.length > 0 && (
                  <ul
                    id={tenantResultsId}
                    className="billing-invoice-tenant-results"
                    aria-label="Resultados da busca de tenants"
                  >
                    {tenantResults.map((tenant) => (
                      <li key={tenant.tenant_id}>
                        <button
                          type="button"
                          onClick={() => onSelectTenant(tenant)}
                          aria-label={`Selecionar tenant ${tenant.name}, schema ${tenant.schema_name}${
                            tenant.plan && (tenant.plan.name || tenant.plan.id)
                              ? `, plano ${tenant.plan.name || tenant.plan.id}`
                              : ''
                          }${
                            tenant.operational_status
                              ? `, status ${tenant.operational_status}`
                              : ''
                          }`}
                        >
                          <strong>{tenant.name}</strong>
                          <span>
                            {tenant.schema_name}
                            {tenant.plan && (tenant.plan.name || tenant.plan.id)
                              ? ` · ${tenant.plan.name || tenant.plan.id}`
                              : ''}
                            {tenant.operational_status ? ` · ${tenant.operational_status}` : ''}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
            <FieldError id="billing-invoice-tenant-search-error" message={errors.tenant_id} />
          </div>
        )}

        {mode === 'create' && selectedTenant && (
          <div className="billing-invoice-form-span">
            <label htmlFor="billing-invoice-subscription">Assinatura vinculada</label>
            {subscriptionsLoading && (
              <div className="billing-invoices-muted" aria-live="polite">Carregando assinaturas…</div>
            )}
            {!subscriptionsLoading && (
              <>
                <select
                  id="billing-invoice-subscription"
                  value={form.subscription_id == null || form.subscription_id === '' ? 'none' : String(form.subscription_id)}
                  onChange={(e) => onChange(
                    'subscription_id',
                    e.target.value === 'none' ? '' : e.target.value,
                  )}
                  aria-invalid={Boolean(errors.subscription_id)}
                  aria-describedby={
                    errors.subscription_id ? 'billing-invoice-subscription-error' : undefined
                  }
                >
                  <option value="none">Sem assinatura vinculada</option>
                  {tenantSubscriptions.map((sub) => (
                    <option key={sub.id} value={String(sub.id)}>
                      #{sub.id}
                      {' · '}
                      {sub.plan_code_snapshot || sub.plan_name_snapshot || '—'}
                      {' · '}
                      {formatSubscriptionStatus(sub.status)}
                      {' · início '}
                      {dateBR(sub.start_date)}
                      {' · vence '}
                      {dateBR(sub.next_due_date)}
                    </option>
                  ))}
                </select>
                {tenantSubscriptions.length === 0 && (
                  <div className="billing-invoices-muted">
                    Nenhuma assinatura disponível para este tenant.
                  </div>
                )}
              </>
            )}
            <FieldError id="billing-invoice-subscription-error" message={errors.subscription_id} />
          </div>
        )}

        <div className="billing-invoice-form-span">
          <label htmlFor="billing-invoice-description">Descrição</label>
          <input
            id="billing-invoice-description"
            type="text"
            value={form.description}
            onChange={(e) => onChange('description', e.target.value)}
            maxLength={500}
            aria-invalid={Boolean(errors.description)}
            aria-describedby={errors.description ? 'billing-invoice-description-error' : undefined}
          />
          <FieldError id="billing-invoice-description-error" message={errors.description} />
        </div>

        <div>
          <label htmlFor="billing-invoice-amount">Valor (BRL)</label>
          <input
            id="billing-invoice-amount"
            type="text"
            inputMode="decimal"
            value={form.amount}
            onChange={(e) => onChange('amount', e.target.value)}
            placeholder="199.90"
            aria-invalid={Boolean(errors.amount)}
            aria-describedby={errors.amount ? 'billing-invoice-amount-error' : undefined}
          />
          <FieldError id="billing-invoice-amount-error" message={errors.amount} />
        </div>

        <div>
          <label htmlFor="billing-invoice-currency">Moeda</label>
          <input id="billing-invoice-currency" type="text" value="BRL" disabled readOnly />
        </div>

        <div>
          <label htmlFor="billing-invoice-due-date">Vencimento</label>
          <input
            id="billing-invoice-due-date"
            type="date"
            value={form.due_date}
            onChange={(e) => onChange('due_date', e.target.value)}
            aria-invalid={Boolean(errors.due_date)}
            aria-describedby={errors.due_date ? 'billing-invoice-due-date-error' : undefined}
          />
          <FieldError id="billing-invoice-due-date-error" message={errors.due_date} />
        </div>

        <div>
          <label htmlFor="billing-invoice-payment-method">Forma de pagamento</label>
          <select
            id="billing-invoice-payment-method"
            value={form.payment_method}
            onChange={(e) => onChange('payment_method', e.target.value)}
            aria-invalid={Boolean(errors.payment_method)}
            aria-describedby={
              errors.payment_method ? 'billing-invoice-payment-method-error' : undefined
            }
          >
            {PAYMENT_METHODS.map((method) => (
              <option key={method} value={method}>{formatPayment(method)}</option>
            ))}
          </select>
          <FieldError id="billing-invoice-payment-method-error" message={errors.payment_method} />
        </div>

        <div className="billing-invoice-form-span">
          <label htmlFor="billing-invoice-notes">Observações</label>
          <textarea
            id="billing-invoice-notes"
            rows={3}
            value={form.notes}
            onChange={(e) => onChange('notes', e.target.value)}
            maxLength={2000}
            aria-invalid={Boolean(errors.notes)}
            aria-describedby={errors.notes ? 'billing-invoice-notes-error' : undefined}
          />
          <FieldError id="billing-invoice-notes-error" message={errors.notes} />
        </div>

        <div className="billing-invoice-form-span">
          <label htmlFor="billing-invoice-reason">Motivo</label>
          <textarea
            id="billing-invoice-reason"
            rows={2}
            value={form.reason}
            onChange={(e) => onChange('reason', e.target.value)}
            maxLength={500}
            placeholder="Descreva o motivo da operação (5 a 500 caracteres)"
            aria-invalid={Boolean(errors.reason)}
            aria-describedby={errors.reason ? 'billing-invoice-reason-error' : undefined}
          />
          <FieldError id="billing-invoice-reason-error" message={errors.reason} />
        </div>
      </div>
    );
  }

  function SaBillingInvoices({ currentUser, onToast, onBillingChanged }) {
    const canManage = typeof saBillingCanManage === 'function'
      ? saBillingCanManage(currentUser)
      : false;

    const [listState, setListState] = useState(() => Object.assign({}, DEFAULT_LIST_STATE));
    const [searchInput, setSearchInput] = useState('');
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const [drawerTab, setDrawerTab] = useState('detalhes');
    const [historyItems, setHistoryItems] = useState([]);
    const [historyMeta, setHistoryMeta] = useState({ count: 0, page: 1, page_size: 20 });
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState(null);
    const [historyExpanded, setHistoryExpanded] = useState({});
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [formError, setFormError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [tenantSearch, setTenantSearch] = useState('');
    const [tenantResults, setTenantResults] = useState([]);
    const [tenantLoading, setTenantLoading] = useState(false);
    const [tenantSubscriptions, setTenantSubscriptions] = useState([]);
    const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
    const [apiParams, setApiParams] = useState(() => saBillingBuildInvoiceQuery(DEFAULT_LIST_STATE));

    const drawerTriggerRef = useRef(null);
    const modalTriggerRef = useRef(null);
    const drawerCloseButtonRef = useRef(null);
    const modalInitialFocusRef = useRef(null);
    const drawerDialogRef = useRef(null);
    const modalDialogRef = useRef(null);
    const drawerTabRefs = useRef({});
    const detailRequestRef = useRef(0);
    const historyRequestRef = useRef(0);
    const activeEntityKeyRef = useRef(null);
    const tenantSearchRef = useRef(0);
    const subscriptionsRequestRef = useRef(0);

    const dueRangeError = validateDateRange(listState.due_date_from, listState.due_date_to);
    const paidRangeError = validateDateRange(listState.paid_from, listState.paid_to);
    const createdRangeError = validateDateRange(listState.created_from, listState.created_to);
    const filtersInvalid = Boolean(dueRangeError || paidRangeError || createdRangeError);

    useEffect(() => {
      if (filtersInvalid) return;
      setApiParams(saBillingBuildInvoiceQuery(listState));
    }, [listState, filtersInvalid]);

    const {
      data,
      meta,
      loading,
      error,
      refetch,
    } = useGlobalBillingInvoices(apiParams);

    const vm = useMemo(() => saBillingBuildInvoicesViewModel({
      results: data,
      count: meta.count,
      page: meta.page,
      page_size: meta.page_size,
    }), [data, meta]);

    const hasActiveFilters = Boolean(
      listState.search
      || listState.display_status
      || listState.status
      || listState.tenant_status
      || listState.plan_code
      || listState.payment_method
      || listState.has_subscription
      || listState.due_date_from
      || listState.due_date_to
      || listState.paid_from
      || listState.paid_to
      || listState.created_from
      || listState.created_to,
    );

    useEffect(() => {
      const timer = setTimeout(() => {
        setListState((prev) => {
          const nextSearch = searchInput.trim().slice(0, 200);
          if (prev.search === nextSearch) return prev;
          return Object.assign({}, prev, { search: nextSearch, page: 1 });
        });
      }, 350);
      return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
      const hasOverlay = Boolean(selectedRow || modal);
      if (!hasOverlay) return undefined;
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const onKey = (event) => {
        if (event.key !== 'Escape') return;
        if (saving) return;
        if (modal) {
          closeModal();
          return;
        }
        if (selectedRow) closeDrawer();
      };
      window.addEventListener('keydown', onKey);
      return () => {
        window.removeEventListener('keydown', onKey);
        document.body.style.overflow = previousOverflow;
      };
    }, [selectedRow, modal, saving]);

    useEffect(() => {
      if (!selectedRow) return undefined;
      if (drawerCloseButtonRef.current) drawerCloseButtonRef.current.focus();
      return undefined;
    }, [selectedRow]);

    useEffect(() => {
      if (!modal) return undefined;
      const timer = setTimeout(() => {
        let target = null;
        if (modal === 'create') {
          target = document.getElementById('billing-invoice-tenant-search');
        } else if (modal === 'edit') {
          target = document.getElementById('billing-invoice-description');
        } else if (modal === 'mark_paid' || modal === 'cancel') {
          target = document.getElementById('billing-invoice-reason');
        }
        if (target && typeof target.focus === 'function') {
          target.focus();
        } else if (modalInitialFocusRef.current && typeof modalInitialFocusRef.current.focus === 'function') {
          modalInitialFocusRef.current.focus();
        }
      }, 0);
      return () => clearTimeout(timer);
    }, [modal]);

    useEffect(() => {
      if (modal || !selectedRow) return undefined;
      const onKey = (event) => {
        trapFocus(event, drawerDialogRef.current);
      };
      const node = drawerDialogRef.current;
      if (node) node.addEventListener('keydown', onKey);
      return () => {
        if (node) node.removeEventListener('keydown', onKey);
      };
    }, [modal, selectedRow]);

    useEffect(() => {
      if (!modal) return undefined;
      const onKey = (event) => {
        trapFocus(event, modalDialogRef.current);
      };
      const node = modalDialogRef.current;
      if (node) node.addEventListener('keydown', onKey);
      return () => {
        if (node) node.removeEventListener('keydown', onKey);
      };
    }, [modal]);

    const handleDrawerTabKeyDown = (event) => {
      const tabs = ['detalhes', 'historico'];
      const handledKeys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
      if (!handledKeys.includes(event.key)) return;
      event.preventDefault();
      const currentIndex = tabs.indexOf(drawerTab);
      if (currentIndex < 0) return;
      let nextIndex = currentIndex;
      if (event.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % tabs.length;
      } else if (event.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = tabs.length - 1;
      }
      const nextTab = tabs[nextIndex];
      setDrawerTab(nextTab);
      const tabEl = drawerTabRefs.current[nextTab];
      if (tabEl && typeof tabEl.focus === 'function') tabEl.focus();
    };

    useEffect(() => {
      if (modal !== 'create') return undefined;
      const trimmed = tenantSearch.trim();
      if (!trimmed || selectedTenant) {
        setTenantResults([]);
        setTenantLoading(false);
        return undefined;
      }
      const requestId = ++tenantSearchRef.current;
      const timer = setTimeout(async () => {
        setTenantLoading(true);
        try {
          const service = tenantService;
          if (!service || typeof service.list !== 'function') {
            setTenantResults([]);
            return;
          }
          const result = await service.list({ search: trimmed, page: 1, page_size: 20 });
          if (requestId !== tenantSearchRef.current) return;
          setTenantResults(Array.isArray(result.data) ? result.data : []);
        } catch (_err) {
          if (requestId !== tenantSearchRef.current) return;
          setTenantResults([]);
        } finally {
          if (requestId === tenantSearchRef.current) setTenantLoading(false);
        }
      }, 350);
      return () => {
        clearTimeout(timer);
        tenantSearchRef.current += 1;
      };
    }, [tenantSearch, modal, selectedTenant]);

    useEffect(() => {
      if (modal !== 'create' || !selectedTenant) {
        setTenantSubscriptions([]);
        setSubscriptionsLoading(false);
        return undefined;
      }
      const requestId = ++subscriptionsRequestRef.current;
      const tenantId = selectedTenant.tenant_id;
      setSubscriptionsLoading(true);
      (async () => {
        try {
          const result = await billingService.listGlobalSubscriptions({
            tenant_id: tenantId,
            page: 1,
            page_size: 100,
            ordering: '-created_at',
          });
          if (requestId !== subscriptionsRequestRef.current) return;
          setTenantSubscriptions(Array.isArray(result.results) ? result.results : []);
        } catch (_err) {
          if (requestId !== subscriptionsRequestRef.current) return;
          setTenantSubscriptions([]);
        } finally {
          if (requestId === subscriptionsRequestRef.current) setSubscriptionsLoading(false);
        }
      })();
      return () => { subscriptionsRequestRef.current += 1; };
    }, [selectedTenant, modal]);

    const setFilterValue = (key, value) => {
      setListState((prev) => Object.assign({}, prev, {
        [key]: value,
        page: key === 'page' ? Math.max(1, Number(value) || 1) : 1,
      }));
    };

    const clearFilters = () => {
      setSearchInput('');
      setListState((prev) => Object.assign({}, DEFAULT_LIST_STATE, {
        page_size: prev.page_size,
      }));
    };

    const loadDetail = async (row) => {
      if (!row) return;
      const requestId = ++detailRequestRef.current;
      const entityKey = billingEntityKey(row.tenant_id, row.id);
      setDetailLoading(true);
      setDetailError(null);
      try {
        const detailResult = await billingService.getTenantInvoiceDetail(
          row.tenant_id,
          row.id,
        );
        if (requestId !== detailRequestRef.current) return;
        if (activeEntityKeyRef.current !== entityKey) return;
        setDetail(detailResult);
      } catch (err) {
        if (requestId !== detailRequestRef.current) return;
        if (activeEntityKeyRef.current !== entityKey) return;
        setDetailError(formatError(err, 'Não foi possível carregar o detalhe da fatura.'));
      } finally {
        if (requestId === detailRequestRef.current) setDetailLoading(false);
      }
    };

    const loadInitialHistory = async (row) => {
      if (!row) return;
      const requestId = ++historyRequestRef.current;
      const entityKey = billingEntityKey(row.tenant_id, row.id);
      setHistoryLoading(true);
      setHistoryError(null);
      setHistoryItems([]);
      setHistoryMeta({ count: 0, page: 1, page_size: 20 });
      try {
        const historyResult = await billingService.getTenantInvoiceHistory(
          row.tenant_id,
          row.id,
          { page: 1, page_size: 20 },
        );
        if (requestId !== historyRequestRef.current) return;
        if (activeEntityKeyRef.current !== entityKey) return;
        setHistoryItems(Array.isArray(historyResult.results) ? historyResult.results : []);
        setHistoryMeta({
          count: Number(historyResult.count) || 0,
          page: Number(historyResult.page) || 1,
          page_size: Number(historyResult.page_size) || 20,
        });
      } catch (err) {
        if (requestId !== historyRequestRef.current) return;
        if (activeEntityKeyRef.current !== entityKey) return;
        setHistoryError(formatError(err, 'Não foi possível carregar o histórico.'));
      } finally {
        if (requestId === historyRequestRef.current) setHistoryLoading(false);
      }
    };

    const loadDetailAndHistory = (row) => {
      if (!row) return;
      setDrawerTab('detalhes');
      setHistoryExpanded({});
      loadDetail(row);
      loadInitialHistory(row);
    };

    const openDrawer = (row, event) => {
      if (event && event.currentTarget) drawerTriggerRef.current = event.currentTarget;
      detailRequestRef.current += 1;
      historyRequestRef.current += 1;
      activeEntityKeyRef.current = billingEntityKey(row.tenant_id, row.id);
      setSelectedRow(row);
      setDetail(null);
      setDetailError(null);
      setHistoryItems([]);
      setHistoryMeta({ count: 0, page: 1, page_size: 20 });
      setHistoryError(null);
      setHistoryExpanded({});
      setDrawerTab('detalhes');
      loadDetailAndHistory(row);
    };

    const closeDrawer = () => {
      if (saving) return;
      detailRequestRef.current += 1;
      historyRequestRef.current += 1;
      activeEntityKeyRef.current = null;
      tenantSearchRef.current += 1;
      subscriptionsRequestRef.current += 1;
      setSelectedRow(null);
      setDetail(null);
      setDetailError(null);
      setDetailLoading(false);
      setHistoryItems([]);
      setHistoryMeta({ count: 0, page: 1, page_size: 20 });
      setHistoryError(null);
      setHistoryLoading(false);
      setHistoryExpanded({});
      setDrawerTab('detalhes');
      setModal(null);
      setForm(null);
      setFormErrors({});
      setFormError(null);
      setSelectedTenant(null);
      setTenantSearch('');
      setTenantResults([]);
      setTenantSubscriptions([]);
      setTenantLoading(false);
      setSubscriptionsLoading(false);
      if (drawerTriggerRef.current && typeof drawerTriggerRef.current.focus === 'function') {
        drawerTriggerRef.current.focus();
      }
    };

    const loadMoreHistory = async () => {
      if (!selectedRow || historyLoading) return;
      const entityKey = billingEntityKey(selectedRow.tenant_id, selectedRow.id);
      const nextPage = (historyMeta.page || 1) + 1;
      const requestId = ++historyRequestRef.current;
      setHistoryLoading(true);
      setHistoryError(null);
      try {
        const historyResult = await billingService.getTenantInvoiceHistory(
          selectedRow.tenant_id,
          selectedRow.id,
          { page: nextPage, page_size: historyMeta.page_size || 20 },
        );
        if (requestId !== historyRequestRef.current) return;
        if (activeEntityKeyRef.current !== entityKey) return;
        const incoming = Array.isArray(historyResult.results) ? historyResult.results : [];
        setHistoryItems((prev) => {
          const seen = {};
          prev.forEach((item) => { seen[item.id] = true; });
          return prev.concat(incoming.filter((item) => !seen[item.id]));
        });
        setHistoryMeta({
          count: Number(historyResult.count) || 0,
          page: Number(historyResult.page) || nextPage,
          page_size: Number(historyResult.page_size) || 20,
        });
      } catch (err) {
        if (requestId !== historyRequestRef.current) return;
        if (activeEntityKeyRef.current !== entityKey) return;
        setHistoryError(formatError(err, 'Não foi possível carregar o histórico.'));
      } finally {
        if (requestId === historyRequestRef.current) setHistoryLoading(false);
      }
    };

    const openCreateModal = (event) => {
      if (event && event.currentTarget) modalTriggerRef.current = event.currentTarget;
      tenantSearchRef.current += 1;
      subscriptionsRequestRef.current += 1;
      setTenantLoading(false);
      setSubscriptionsLoading(false);
      setModal('create');
      setSelectedTenant(null);
      setTenantSearch('');
      setTenantResults([]);
      setTenantSubscriptions([]);
      setForm({
        description: '',
        amount: '',
        due_date: '',
        subscription_id: '',
        payment_method: 'not_informed',
        notes: '',
        reason: '',
      });
      setFormErrors({});
      setFormError(null);
    };

    const openEditModal = (event) => {
      if (!canManage || !detail || detail.status !== 'open') return;
      if (event && event.currentTarget) modalTriggerRef.current = event.currentTarget;
      setModal('edit');
      setForm({
        description: detail.description || '',
        amount: detail.amount != null ? String(detail.amount) : '',
        due_date: detail.due_date || '',
        payment_method: detail.payment_method || 'not_informed',
        notes: detail.notes || '',
        reason: '',
      });
      setFormErrors({});
      setFormError(null);
    };

    const openMarkPaidModal = (event) => {
      if (!canManage || !detail || detail.status !== 'open') return;
      if (event && event.currentTarget) modalTriggerRef.current = event.currentTarget;
      setModal('mark_paid');
      setForm({ reason: '' });
      setFormErrors({});
      setFormError(null);
    };

    const openCancelModal = (event) => {
      if (!canManage || !detail || detail.status !== 'open') return;
      if (event && event.currentTarget) modalTriggerRef.current = event.currentTarget;
      setModal('cancel');
      setForm({ reason: '' });
      setFormErrors({});
      setFormError(null);
    };

    const closeModal = () => {
      if (saving) return;
      tenantSearchRef.current += 1;
      subscriptionsRequestRef.current += 1;
      setTenantLoading(false);
      setSubscriptionsLoading(false);
      setModal(null);
      setForm(null);
      setFormErrors({});
      setFormError(null);
      setSelectedTenant(null);
      setTenantSearch('');
      setTenantResults([]);
      setTenantSubscriptions([]);
      if (modalTriggerRef.current && typeof modalTriggerRef.current.focus === 'function') {
        modalTriggerRef.current.focus();
      }
    };

    const selectTenant = (tenant) => {
      tenantSearchRef.current += 1;
      subscriptionsRequestRef.current += 1;
      setTenantLoading(false);
      setSelectedTenant(tenant);
      setTenantSearch('');
      setTenantResults([]);
      setTenantSubscriptions([]);
      setForm((prev) => (prev ? Object.assign({}, prev, { subscription_id: '' }) : prev));
    };

    const clearSelectedTenant = () => {
      tenantSearchRef.current += 1;
      subscriptionsRequestRef.current += 1;
      setTenantLoading(false);
      setSubscriptionsLoading(false);
      setSelectedTenant(null);
      setTenantResults([]);
      setTenantSubscriptions([]);
      setForm((prev) => (prev ? Object.assign({}, prev, { subscription_id: '' }) : prev));
    };

    const REFRESH_PARTIAL_WARNING = 'Operação concluída, mas não foi possível atualizar todos os dados da tela.';

    const refreshAfterWrite = async (invoicePayload, rowOverride) => {
      if (invoicePayload) setDetail(invoicePayload);
      const row = rowOverride || selectedRow;
      if (row) {
        const entityKey = billingEntityKey(row.tenant_id, row.id);
        const requestId = ++historyRequestRef.current;
        try {
          const historyResult = await billingService.getTenantInvoiceHistory(
            row.tenant_id,
            row.id,
            { page: 1, page_size: 20 },
          );
          const canApplyHistory =
            requestId === historyRequestRef.current
            && activeEntityKeyRef.current === entityKey;
          if (canApplyHistory) {
            setHistoryItems(Array.isArray(historyResult.results) ? historyResult.results : []);
            setHistoryMeta({
              count: Number(historyResult.count) || 0,
              page: Number(historyResult.page) || 1,
              page_size: Number(historyResult.page_size) || 20,
            });
            setHistoryError(null);
          }
        } catch (_err) {
          // Histórico é best-effort.
        }
      }

      let refreshWarning = null;
      if (typeof refetch === 'function') {
        try {
          const listRefresh = await refetch();
          if (
            listRefresh
            && listRefresh.ok === false
            && listRefresh.stale !== true
          ) {
            refreshWarning = REFRESH_PARTIAL_WARNING;
          }
        } catch (_err) {
          refreshWarning = REFRESH_PARTIAL_WARNING;
        }
      }
      if (typeof onBillingChanged === 'function') {
        try {
          const overviewRefresh = await Promise.resolve(
            onBillingChanged(),
          );
          if (
            overviewRefresh
            && overviewRefresh.ok === false
            && overviewRefresh.stale !== true
          ) {
            refreshWarning = REFRESH_PARTIAL_WARNING;
          }
        } catch (_err) {
          if (!refreshWarning) refreshWarning = REFRESH_PARTIAL_WARNING;
        }
      }
      return refreshWarning;
    };

    const isInvoiceModalSubmitDisabled = () => {
      if (modal === 'create') return !canManage;
      if (modal === 'edit' || modal === 'mark_paid' || modal === 'cancel') {
        return !canManage || !detail || detail.status !== 'open';
      }
      return false;
    };

    const handleCreate = async () => {
      if (!canManage) return;
      const validation = saBillingValidateInvoiceForm(
        Object.assign({}, form, { tenant_id: selectedTenant && selectedTenant.tenant_id }),
        'create',
      );
      setFormErrors(validation.errors);
      if (!validation.valid) {
        focusFirstFormError(validation.errors);
        return;
      }
      if (!selectedTenant || !isPositiveIntegerId(selectedTenant.tenant_id)) {
        const tenantErrors = Object.assign({}, validation.errors, { tenant_id: 'Selecione um tenant.' });
        setFormErrors(tenantErrors);
        focusFirstFormError(tenantErrors);
        return;
      }
      setSaving(true);
      setFormError(null);
      try {
        const result = await billingService.createTenantInvoice(
          selectedTenant.tenant_id,
          validation.payload,
        );
        onToast && onToast('Fatura criada com sucesso.');
        closeModal();
        drawerTriggerRef.current = modalTriggerRef.current;
        if (result && result.invoice) {
          const row = Object.assign({}, result.invoice, {
            tenant_id: selectedTenant.tenant_id,
            tenant_name: selectedTenant.name,
            tenant_schema: selectedTenant.schema_name,
          });
          setDrawerTab('detalhes');
          setHistoryExpanded({});
          setSelectedRow(row);
          activeEntityKeyRef.current = billingEntityKey(selectedTenant.tenant_id, result.invoice.id);
          const refreshWarning = await refreshAfterWrite(result.invoice, row);
          if (refreshWarning && onToast) onToast(refreshWarning);
        } else {
          const refreshWarning = await refreshAfterWrite(null);
          if (refreshWarning && onToast) onToast(refreshWarning);
        }
      } catch (err) {
        setFormError(formatError(err, 'Não foi possível criar a fatura.'));
      } finally {
        setSaving(false);
      }
    };

    const handleEdit = async () => {
      if (!canManage) return;
      if (!selectedRow || !detail) return;
      if (detail.status !== 'open') {
        setFormError('A fatura não está mais aberta. Atualize os dados antes de continuar.');
        await loadDetail(selectedRow);
        return;
      }
      const validation = saBillingValidateInvoiceForm(form, 'edit', detail);
      setFormErrors(validation.errors);
      if (!validation.valid) {
        focusFirstFormError(validation.errors);
        return;
      }
      setSaving(true);
      setFormError(null);
      try {
        const result = await billingService.updateTenantInvoice(
          selectedRow.tenant_id,
          selectedRow.id,
          validation.payload,
        );
        if (result && result.noop === true) {
          onToast && onToast('Nenhuma alteração foi necessária.');
        } else {
          onToast && onToast('Fatura atualizada com sucesso.');
        }
        closeModal();
        const refreshWarning = await refreshAfterWrite(result && result.invoice);
        if (refreshWarning && onToast) onToast(refreshWarning);
      } catch (err) {
        const message = formatError(err, 'Não foi possível atualizar a fatura.');
        setFormError(message);
        if (err && err.status === 409) {
          try {
            const refreshed = await billingService.getTenantInvoiceDetail(
              selectedRow.tenant_id,
              selectedRow.id,
            );
            setDetail(refreshed);
          } catch (_refreshErr) {
            // keep previous detail
          }
        }
      } finally {
        setSaving(false);
      }
    };

    const handleMarkPaid = async () => {
      if (!canManage) return;
      if (!selectedRow || !detail) return;
      if (detail.status !== 'open') {
        setFormError('A fatura não está mais aberta. Atualize os dados antes de continuar.');
        await loadDetail(selectedRow);
        return;
      }
      const reason = form && form.reason != null ? String(form.reason).trim() : '';
      const errors = {};
      if (reason.length < 5 || reason.length > 500) {
        errors.reason = 'Motivo deve ter entre 5 e 500 caracteres.';
      }
      setFormErrors(errors);
      if (Object.keys(errors).length) {
        focusFirstFormError(errors);
        return;
      }

      setSaving(true);
      setFormError(null);
      try {
        const result = await billingService.markTenantInvoicePaid(
          selectedRow.tenant_id,
          selectedRow.id,
          { reason },
        );
        onToast && onToast('Fatura marcada administrativamente como paga.');
        closeModal();
        const refreshWarning = await refreshAfterWrite(result && result.invoice);
        if (refreshWarning && onToast) onToast(refreshWarning);
      } catch (err) {
        const message = formatError(err, 'Não foi possível marcar a fatura como paga.');
        setFormError(message);
        if (err && err.status === 409) {
          try {
            const refreshed = await billingService.getTenantInvoiceDetail(
              selectedRow.tenant_id,
              selectedRow.id,
            );
            setDetail(refreshed);
          } catch (_refreshErr) {
            // keep previous detail
          }
        }
      } finally {
        setSaving(false);
      }
    };

    const handleCancel = async () => {
      if (!canManage) return;
      if (!selectedRow || !detail) return;
      if (detail.status !== 'open') {
        setFormError('A fatura não está mais aberta. Atualize os dados antes de continuar.');
        await loadDetail(selectedRow);
        return;
      }
      const reason = form && form.reason != null ? String(form.reason).trim() : '';
      const errors = {};
      if (reason.length < 5 || reason.length > 500) {
        errors.reason = 'Motivo deve ter entre 5 e 500 caracteres.';
      }
      setFormErrors(errors);
      if (Object.keys(errors).length) {
        focusFirstFormError(errors);
        return;
      }

      setSaving(true);
      setFormError(null);
      try {
        const result = await billingService.cancelTenantInvoice(
          selectedRow.tenant_id,
          selectedRow.id,
          { reason },
        );
        onToast && onToast('Fatura cancelada com sucesso.');
        closeModal();
        const refreshWarning = await refreshAfterWrite(result && result.invoice);
        if (refreshWarning && onToast) onToast(refreshWarning);
      } catch (err) {
        const message = formatError(err, 'Não foi possível cancelar a fatura.');
        setFormError(message);
        if (err && err.status === 409) {
          try {
            const refreshed = await billingService.getTenantInvoiceDetail(
              selectedRow.tenant_id,
              selectedRow.id,
            );
            setDetail(refreshed);
          } catch (_refreshErr) {
            // keep previous detail
          }
        }
      } finally {
        setSaving(false);
      }
    };

    const isInitialLoading = loading && (!data || data.length === 0) && !error;
    const showStaleError = Boolean(error && data && data.length > 0);
    const showEmpty = !loading && !error && !filtersInvalid && vm.is_empty;

    const detailDisplayStatus = detail
      ? (detail.display_status || detail.status || '')
      : '';

    return (
      <div className="billing-invoices">
        <div className="billing-invoices-header">
          <div>
            <h2>Faturas</h2>
            <p>Faturas internas cadastradas para os tenants da plataforma.</p>
            <div className="billing-invoices-count">
              {meta.count} registro{meta.count === 1 ? '' : 's'}
            </div>
          </div>
          <div className="billing-invoices-actions">
            <button
              type="button"
              className="btn"
              onClick={() => refetch && refetch()}
              disabled={loading}
              aria-busy={loading || undefined}
            >
              {loading ? 'Atualizando…' : 'Atualizar'}
            </button>
            {canManage && (
              <button type="button" className="btn primary" onClick={openCreateModal}>
                Nova fatura
              </button>
            )}
          </div>
        </div>

        <div className="billing-invoices-toolbar">
          <div className="billing-invoices-search">
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value.slice(0, 200))}
              placeholder="Buscar por número, tenant, schema ou descrição"
              maxLength={200}
              aria-label="Buscar por número, tenant, schema ou descrição"
            />
            {searchInput && (
              <button type="button" className="btn" onClick={() => setSearchInput('')}>
                Limpar
              </button>
            )}
          </div>

          <div className="billing-invoices-filters">
            <label>
              <span>Situação</span>
              <select
                value={listState.display_status}
                onChange={(e) => setFilterValue('display_status', e.target.value)}
              >
                <option value="">Todas</option>
                {DISPLAY_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {formatDisplayStatus(status)}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Status persistido</span>
              <select
                value={listState.status}
                onChange={(e) => setFilterValue('status', e.target.value)}
              >
                <option value="">Todos</option>
                {PERSISTED_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {PERSISTED_STATUS_LABELS[status]}
                  </option>
                ))}
              </select>
              <span className="billing-invoices-filter-hint">
                &quot;Vencida&quot; é calculada e permanece persistida como &quot;Aberta&quot;.
              </span>
            </label>

            <label>
              <span>Status do tenant</span>
              <select
                value={listState.tenant_status}
                onChange={(e) => setFilterValue('tenant_status', e.target.value)}
              >
                <option value="">Todos</option>
                {TENANT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {TENANT_STATUS_LABELS[status]}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Forma de pagamento</span>
              <select
                value={listState.payment_method}
                onChange={(e) => setFilterValue('payment_method', e.target.value)}
              >
                <option value="">Todas</option>
                {PAYMENT_METHODS.map((method) => (
                  <option key={method} value={method}>{formatPayment(method)}</option>
                ))}
              </select>
            </label>

            <label>
              <span>Plano</span>
              <input
                type="text"
                value={listState.plan_code}
                onChange={(e) => setFilterValue('plan_code', e.target.value)}
                placeholder="Código do plano"
              />
            </label>

            <label>
              <span>Vínculo com assinatura</span>
              <select
                value={listState.has_subscription}
                onChange={(e) => setFilterValue('has_subscription', e.target.value)}
              >
                <option value="">Todas</option>
                <option value="true">Com assinatura</option>
                <option value="false">Sem assinatura</option>
              </select>
            </label>

            <label>
              <span>Ordenação</span>
              <select
                value={listState.ordering}
                onChange={(e) => setFilterValue('ordering', e.target.value)}
              >
                {ORDERING_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="billing-invoices-advanced-toggle">
            <button
              type="button"
              className="btn"
              onClick={() => setShowAdvanced((prev) => !prev)}
              aria-expanded={showAdvanced}
              aria-controls="billing-invoices-advanced-filters"
            >
              {showAdvanced ? 'Ocultar mais filtros' : 'Mais filtros'}
            </button>
            <button type="button" className="btn" onClick={clearFilters}>
              Limpar filtros
            </button>
          </div>

          {showAdvanced && (
            <div id="billing-invoices-advanced-filters" className="billing-invoices-advanced">
              <label>
                <span>Vencimento de</span>
                <input
                  type="date"
                  value={listState.due_date_from}
                  onChange={(e) => setFilterValue('due_date_from', e.target.value)}
                />
              </label>
              <label>
                <span>Vencimento até</span>
                <input
                  type="date"
                  value={listState.due_date_to}
                  onChange={(e) => setFilterValue('due_date_to', e.target.value)}
                />
              </label>
              <label>
                <span>Pagamento de</span>
                <input
                  type="date"
                  value={listState.paid_from}
                  onChange={(e) => setFilterValue('paid_from', e.target.value)}
                />
              </label>
              <label>
                <span>Pagamento até</span>
                <input
                  type="date"
                  value={listState.paid_to}
                  onChange={(e) => setFilterValue('paid_to', e.target.value)}
                />
              </label>
              <label>
                <span>Criada de</span>
                <input
                  type="date"
                  value={listState.created_from}
                  onChange={(e) => setFilterValue('created_from', e.target.value)}
                />
              </label>
              <label>
                <span>Criada até</span>
                <input
                  type="date"
                  value={listState.created_to}
                  onChange={(e) => setFilterValue('created_to', e.target.value)}
                />
              </label>
              {(dueRangeError || paidRangeError || createdRangeError) && (
                <div className="billing-invoices-alert" role="alert">
                  {dueRangeError || paidRangeError || createdRangeError}
                </div>
              )}
            </div>
          )}
        </div>

        {showStaleError && (
          <div className="billing-invoices-alert" role="alert">
            <span>{error}</span>
            <button type="button" className="btn" onClick={() => refetch && refetch()}>
              Tentar novamente
            </button>
          </div>
        )}

        {filtersInvalid && (
          <div className="billing-invoices-alert" role="alert">
            Corrija o intervalo de datas antes de consultar a lista.
          </div>
        )}

        {isInitialLoading && (
          <div
            className="billing-invoices-skeleton"
            aria-busy="true"
            aria-live="polite"
          >
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="billing-invoices-skel-row" />
            ))}
          </div>
        )}

        {!isInitialLoading && error && (!data || data.length === 0) && (
          typeof ApiErrorState === 'function'
            ? <ApiErrorState error={error} onRetry={refetch} />
            : (
              <div className="billing-invoices-alert" role="alert">
                <span>{error}</span>
                <button type="button" className="btn" onClick={() => refetch && refetch()}>
                  Tentar novamente
                </button>
              </div>
            )
        )}

        {showEmpty && !hasActiveFilters && (
          <div className="hint-card billing-invoices-empty">
            <div className="billing-invoices-empty-title">
              Nenhuma fatura interna cadastrada
            </div>
            <p>As faturas aparecerão aqui após o primeiro cadastro.</p>
            {canManage && (
              <button type="button" className="btn primary" onClick={openCreateModal}>
                Nova fatura
              </button>
            )}
          </div>
        )}

        {showEmpty && hasActiveFilters && (
          <div className="hint-card billing-invoices-empty">
            <div className="billing-invoices-empty-title">
              Nenhuma fatura encontrada
            </div>
            <button type="button" className="btn" onClick={clearFilters}>
              Limpar filtros
            </button>
          </div>
        )}

        {!isInitialLoading && !showEmpty && !(error && (!data || data.length === 0)) && !filtersInvalid && (
          <>
            <div className="billing-invoices-table-wrap">
              <table className="billing-invoices-table">
                <caption className="billing-sr-only">
                  Lista de faturas internas dos tenants
                </caption>
                <thead>
                  <tr>
                    <th>Número</th>
                    <th>Tenant</th>
                    <th>Descrição</th>
                    <th className="billing-invoices-col-plan">Plano</th>
                    <th>Situação</th>
                    <th>Valor</th>
                    <th>Vencimento</th>
                    <th>Pagamento</th>
                    <th className="billing-invoices-col-subscription">Assinatura</th>
                    <th>Status do tenant</th>
                    <th className="billing-invoices-col-updated">Atualização</th>
                  </tr>
                </thead>
                <tbody>
                  {vm.rows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={(e) => openDrawer(row, e)}
                    >
                      <td>
                        <button
                          type="button"
                          className="billing-row-trigger"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDrawer(row, e);
                          }}
                          aria-label={`Abrir fatura ${row.number_label} do tenant ${row.tenant_name || 'sem nome'}`}
                        >
                          {row.number_label}
                        </button>
                      </td>
                      <td>
                        <div className="billing-invoices-tenant-name">{row.tenant_name || '—'}</div>
                        <div className="billing-invoices-tenant-schema">{row.tenant_schema || '—'}</div>
                      </td>
                      <td>
                        <div
                          className="billing-invoices-desc"
                          title={row.description_label}
                        >
                          {row.description_label}
                        </div>
                      </td>
                      <td className="billing-invoices-col-plan">{row.plan_label}</td>
                      <td>
                        <span className={displayStatusBadgeClass(row.display_status_tone)}>
                          <span className="dot" />
                          {row.display_status_label}
                        </span>
                      </td>
                      <td>{row.amount_formatted}</td>
                      <td>{row.due_date_formatted}</td>
                      <td>{row.payment_method_label}</td>
                      <td className="billing-invoices-col-subscription">{row.subscription_label}</td>
                      <td>{row.tenant_status_label}</td>
                      <td className="billing-invoices-col-updated">{row.updated_at_formatted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="billing-invoices-mobile-list">
              {vm.rows.map((row) => (
                <button
                  key={`m-${row.id}`}
                  type="button"
                  className="billing-invoices-mobile-card"
                  onClick={(e) => openDrawer(row, e)}
                  aria-label={`Fatura ${row.number_label}, tenant ${row.tenant_name || 'sem nome'}, situação ${row.display_status_label}, valor ${row.amount_formatted}, vencimento ${row.due_date_formatted}`}
                >
                  <div className="billing-invoices-mobile-top">
                    <strong>{row.number_label}</strong>
                    <span className={displayStatusBadgeClass(row.display_status_tone)}>
                      <span className="dot" />
                      {row.display_status_label}
                    </span>
                  </div>
                  <div className="billing-invoices-muted">{row.tenant_label}</div>
                  <div>{row.description_label}</div>
                  <div>{row.amount_formatted} · vence {row.due_date_formatted}</div>
                </button>
              ))}
            </div>

            <nav className="billing-invoices-pagination" aria-label="Paginação de faturas">
              <div aria-live="polite" aria-atomic="true">
                {meta.count} registro{meta.count === 1 ? '' : 's'} · Página {meta.page} de {meta.total_pages}
              </div>
              <div className="billing-invoices-pagination-controls">
                <button
                  type="button"
                  className="btn"
                  disabled={meta.page <= 1 || loading}
                  onClick={() => setFilterValue('page', meta.page - 1)}
                >
                  Anterior
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={meta.page >= meta.total_pages || loading}
                  onClick={() => setFilterValue('page', meta.page + 1)}
                >
                  Próxima
                </button>
                <select
                  value={listState.page_size}
                  onChange={(e) => setFilterValue('page_size', Number(e.target.value))}
                  aria-label="Itens por página"
                >
                  {[20, 50, 100].map((size) => (
                    <option key={size} value={size}>{size} / página</option>
                  ))}
                </select>
              </div>
            </nav>
          </>
        )}

        {selectedRow && (
          <div
            className="billing-invoice-drawer-backdrop"
            onClick={() => { if (!saving) closeDrawer(); }}
          >
            <div
              ref={drawerDialogRef}
              className="billing-invoice-drawer"
              role="dialog"
              aria-modal={modal ? undefined : 'true'}
              aria-hidden={modal ? 'true' : undefined}
              aria-labelledby="billing-invoice-drawer-title"
              style={modal ? { pointerEvents: 'none' } : undefined}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="billing-invoice-drawer-header">
                <div>
                  <h3 id="billing-invoice-drawer-title">
                    Fatura {selectedRow.number || `#${selectedRow.id}`}
                  </h3>
                  <div className="billing-invoices-muted">
                    {selectedRow.tenant_name || '—'}
                    {selectedRow.tenant_schema ? ` · ${selectedRow.tenant_schema}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn"
                  ref={drawerCloseButtonRef}
                  aria-label="Fechar detalhe da fatura"
                  onClick={closeDrawer}
                  disabled={saving}
                >
                  Fechar
                </button>
              </div>

              <div
                className="billing-invoice-drawer-tabs"
                role="tablist"
                aria-label="Seções da fatura"
                onKeyDown={handleDrawerTabKeyDown}
              >
                <button
                  type="button"
                  ref={(el) => { drawerTabRefs.current.detalhes = el; }}
                  id="billing-invoice-detail-tab"
                  role="tab"
                  className={drawerTab === 'detalhes' ? 'is-active' : ''}
                  aria-selected={drawerTab === 'detalhes'}
                  aria-controls="billing-invoice-detail-panel"
                  tabIndex={drawerTab === 'detalhes' ? 0 : -1}
                  onClick={() => setDrawerTab('detalhes')}
                >
                  Detalhes
                </button>
                <button
                  type="button"
                  ref={(el) => { drawerTabRefs.current.historico = el; }}
                  id="billing-invoice-history-tab"
                  role="tab"
                  className={drawerTab === 'historico' ? 'is-active' : ''}
                  aria-selected={drawerTab === 'historico'}
                  aria-controls="billing-invoice-history-panel"
                  tabIndex={drawerTab === 'historico' ? 0 : -1}
                  onClick={() => setDrawerTab('historico')}
                >
                  Histórico
                </button>
              </div>

              <div className="billing-invoice-drawer-body">
                {drawerTab === 'detalhes' && (
                  <div
                    id="billing-invoice-detail-panel"
                    role="tabpanel"
                    aria-labelledby="billing-invoice-detail-tab"
                    tabIndex={0}
                  >
                    {detailLoading && (
                      <div aria-busy="true" aria-live="polite">Carregando detalhe…</div>
                    )}
                    {detailError && (
                      <div className="billing-invoices-alert" role="alert">
                        <span>{detailError}</span>
                        <button type="button" className="btn" onClick={() => loadDetail(selectedRow)}>
                          Tentar novamente
                        </button>
                      </div>
                    )}
                    {!detailLoading && !detailError && detail && (
                      <>
                        <div className="billing-invoice-detail-grid">
                          <DetailField label="Tenant ID" value={detail.tenant_id} />
                          <DetailField label="ID" value={detail.id} />
                          <DetailField label="Número" value={detail.number} />
                          <DetailField label="Status persistido" value={PERSISTED_STATUS_LABELS[detail.status] || detail.status} />
                          <DetailField label="Situação" value={formatDisplayStatus(detailDisplayStatus)} />
                          <DetailField label="Vencida" value={detail.is_overdue === true ? 'Sim' : detail.is_overdue === false ? 'Não' : '—'} />
                          <DetailField label="Descrição" value={detail.description} />
                          <DetailField label="Valor" value={money(detail.amount)} />
                          <DetailField label="Moeda" value={detail.currency} />
                          <DetailField label="Vencimento" value={dateBR(detail.due_date)} />
                          <DetailField label="Assinatura" value={
                            detail.subscription_id != null && detail.subscription_id !== ''
                              ? `#${detail.subscription_id}`
                              : 'Sem assinatura vinculada'
                          } />
                          <DetailField label="Plano (snapshot)" value={detail.plan_code_snapshot} />
                          <DetailField label="Pagamento" value={formatPayment(detail.payment_method)} />
                          <DetailField label="Observações" value={detail.notes} />
                          <DetailField label="Criada em" value={dateTimeBR(detail.created_at)} />
                          <DetailField label="Atualizada em" value={dateTimeBR(detail.updated_at)} />
                          <DetailField label="Criada por" value={actorLabel(detail.created_by)} />
                          <DetailField label="Atualizada por" value={actorLabel(detail.updated_by)} />
                          <DetailField label="Marcada como paga em" value={dateTimeBR(detail.paid_at)} />
                          <DetailField label="Marcada como paga por" value={actorLabel(detail.paid_by)} />
                          <DetailField label="Cancelada em" value={dateTimeBR(detail.cancelled_at)} />
                          <DetailField label="Cancelada por" value={actorLabel(detail.cancelled_by)} />
                          <DetailField label="Motivo do cancelamento" value={detail.cancelled_reason} />
                        </div>

                        {canManage && detail.status === 'open' && (
                          <div className="billing-invoice-drawer-actions">
                            <button type="button" className="btn" onClick={openEditModal}>
                              Editar fatura
                            </button>
                            <button type="button" className="btn" onClick={openMarkPaidModal}>
                              Marcar como paga
                            </button>
                            <button type="button" className="btn danger" onClick={openCancelModal}>
                              Cancelar fatura
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {drawerTab === 'historico' && (
                  <div
                    id="billing-invoice-history-panel"
                    role="tabpanel"
                    aria-labelledby="billing-invoice-history-tab"
                    tabIndex={0}
                    className="billing-invoice-history"
                  >
                    {historyLoading && historyItems.length === 0 && !historyError && (
                      <div aria-busy="true" aria-live="polite">Carregando histórico…</div>
                    )}
                    {historyError && (
                      <div className="billing-invoices-alert" role="alert">
                        <span>{historyError}</span>
                        <button type="button" className="btn" onClick={() => loadInitialHistory(selectedRow)}>
                          Tentar novamente
                        </button>
                      </div>
                    )}
                    {historyItems.length === 0 && !historyLoading && !historyError && (
                      <div className="billing-invoices-muted">Nenhum evento registrado.</div>
                    )}
                    {historyItems.map((event) => {
                      const diffs = snapshotDiffEntries(event.before, event.after);
                      const expanded = historyExpanded[event.id];
                      return (
                        <div key={event.id} className="billing-invoice-history-item">
                          <div className="billing-invoice-history-main">
                            <strong>
                              {HISTORY_EVENT_LABELS[event.event_type] || event.event_type}
                            </strong>
                            <span>{dateTimeBR(event.created_at)}</span>
                          </div>
                          <div className="billing-invoices-muted">
                            Ator: {actorLabel(event.actor)}
                          </div>
                          {event.reason && (
                            <div>Motivo: {event.reason}</div>
                          )}
                          {diffs.length > 0 && (
                            <div>
                              <button
                                type="button"
                                className="btn"
                                onClick={() => setHistoryExpanded((prev) => Object.assign({}, prev, {
                                  [event.id]: !prev[event.id],
                                }))}
                              >
                                {expanded ? 'Ocultar alterações' : 'Ver alterações'}
                              </button>
                              {expanded && (
                                <ul className="billing-invoice-history-diff">
                                  {diffs.map((diff) => (
                                    <li key={diff.key}>
                                      <code>{diff.key}</code>: {diff.before} → {diff.after}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {historyItems.length > 0 && historyItems.length < historyMeta.count && (
                      <button
                        type="button"
                        className="btn"
                        onClick={loadMoreHistory}
                        disabled={historyLoading}
                      >
                        {historyLoading ? 'Carregando…' : 'Carregar mais'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {modal && (
          <div
            className="billing-invoice-modal-backdrop"
            onClick={() => { if (!saving) closeModal(); }}
          >
            <div
              ref={modalDialogRef}
              className="billing-invoice-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby={
                modal === 'create' ? 'billing-invoice-modal-create-title'
                  : modal === 'edit' ? 'billing-invoice-modal-edit-title'
                    : modal === 'mark_paid' ? 'billing-invoice-modal-mark-paid-title'
                      : 'billing-invoice-modal-cancel-title'
              }
              onClick={(e) => e.stopPropagation()}
            >
              <div className="billing-invoice-modal-header">
                <h3
                  id={
                    modal === 'create' ? 'billing-invoice-modal-create-title'
                      : modal === 'edit' ? 'billing-invoice-modal-edit-title'
                        : modal === 'mark_paid' ? 'billing-invoice-modal-mark-paid-title'
                          : 'billing-invoice-modal-cancel-title'
                  }
                >
                  {modal === 'create' && 'Nova fatura'}
                  {modal === 'edit' && 'Editar fatura'}
                  {modal === 'mark_paid' && 'Marcar fatura como paga'}
                  {modal === 'cancel' && 'Cancelar fatura'}
                </h3>
                <button
                  type="button"
                  className="btn"
                  ref={modal === 'create' || modal === 'edit' ? modalInitialFocusRef : undefined}
                  onClick={closeModal}
                  disabled={saving}
                  aria-label={
                    modal === 'create' ? 'Fechar modal de criação da fatura'
                      : modal === 'edit' ? 'Fechar modal de edição da fatura'
                        : modal === 'mark_paid' ? 'Fechar modal de marcação da fatura como paga'
                          : 'Fechar modal de cancelamento da fatura'
                  }
                >
                  Fechar
                </button>
              </div>

              <div className="billing-invoice-modal-body">
                {modal === 'mark_paid' && (
                  <p>
                    Esta é uma marcação administrativa interna. Ela não consulta nem confirma
                    pagamento em gateway.
                  </p>
                )}
                {modal === 'cancel' && (
                  <p>Cancela apenas o registro administrativo interno desta fatura.</p>
                )}

                {(modal === 'create' || modal === 'edit') && form && (
                  <InvoiceFormFields
                    form={form}
                    errors={formErrors}
                    onChange={(key, value) => setForm((prev) => Object.assign({}, prev, { [key]: value }))}
                    mode={modal === 'create' ? 'create' : 'edit'}
                    selectedTenant={selectedTenant}
                    tenantSearch={tenantSearch}
                    onTenantSearchChange={setTenantSearch}
                    tenantResults={tenantResults}
                    tenantLoading={tenantLoading}
                    onSelectTenant={selectTenant}
                    onClearTenant={clearSelectedTenant}
                    tenantSubscriptions={tenantSubscriptions}
                    subscriptionsLoading={subscriptionsLoading}
                  />
                )}

                {(modal === 'mark_paid' || modal === 'cancel') && form && (
                  <div className="billing-invoice-form-grid">
                    <div className="billing-invoice-form-span">
                      <label htmlFor="billing-invoice-reason">Motivo</label>
                      <textarea
                        id="billing-invoice-reason"
                        ref={modalInitialFocusRef}
                        rows={3}
                        value={form.reason}
                        onChange={(e) => setForm(Object.assign({}, form, { reason: e.target.value }))}
                        maxLength={500}
                        placeholder="Descreva o motivo (5 a 500 caracteres)"
                        aria-invalid={Boolean(formErrors.reason)}
                        aria-describedby={
                          formErrors.reason ? 'billing-invoice-reason-error' : undefined
                        }
                      />
                      <FieldError id="billing-invoice-reason-error" message={formErrors.reason} />
                    </div>
                  </div>
                )}

                {formError && (
                  <div className="billing-invoices-alert" role="alert">{formError}</div>
                )}
              </div>

              <div className="billing-invoice-modal-footer">
                <button type="button" className="btn" onClick={closeModal} disabled={saving}>
                  Fechar
                </button>
                {modal === 'create' && (
                  <button
                    type="button"
                    className="btn primary"
                    onClick={handleCreate}
                    disabled={saving || isInvoiceModalSubmitDisabled()}
                  >
                    {saving ? 'Salvando…' : 'Criar fatura'}
                  </button>
                )}
                {modal === 'edit' && (
                  <button
                    type="button"
                    className="btn primary"
                    onClick={handleEdit}
                    disabled={saving || isInvoiceModalSubmitDisabled()}
                  >
                    {saving ? 'Salvando…' : 'Salvar alterações'}
                  </button>
                )}
                {modal === 'mark_paid' && (
                  <button
                    type="button"
                    className="btn primary"
                    onClick={handleMarkPaid}
                    disabled={saving || isInvoiceModalSubmitDisabled()}
                  >
                    {saving ? 'Salvando…' : 'Marcar como paga'}
                  </button>
                )}
                {modal === 'cancel' && (
                  <button
                    type="button"
                    className="btn danger"
                    onClick={handleCancel}
                    disabled={saving || isInvoiceModalSubmitDisabled()}
                  >
                    {saving ? 'Salvando…' : 'Cancelar fatura'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

export {
  SaBillingInvoices,
  saBillingBuildInvoiceQuery,
  saBillingBuildInvoicesViewModel,
  saBillingValidateInvoiceForm,
};
